import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogFooter,
  Button,
  ScrollArea,
} from "@pushpress/pushpress-ui";
import MembersTable from "./MembersTable";
import { Plan } from "../../../../apis/subscriptionApi";
import { useAnalytics } from "../../../../components/providers/AnalyticsProvider";
import { useFeatureFlag } from "../../../../context/features";
import { MIGRATION_ASSISTANT_ASYNC_ACTIVATION } from "../../../../context/features/features.utils";
import { defaultToastOptions } from "../../../Appointments/BookAppointmentDrawer/BookAppointmentDrawer.utils";
import { useActivateMembers } from "../../api/migrationAssistantApi.hooks";
import ReviewAndActivate from "./steps/ReviewAndActivate";
import Stepper from "./Stepper";
import SelectActivatingUsers from "./steps/SelectActivatingUsers";
import SelectPaymentCollectionUsers from "./steps/SelectPaymentCollectionUsers";
import { activationSteps } from "../../helpers";
import {
  MemberDataType,
  PaymentSelection,
  PaymentSelectionType,
  StepIdType,
  StepType,
} from "../../types";

export default function ReviewAndActivateModal({
  membersData,
  plans,
  isModalOpen,
  setIsModalOpen,
  setShowActivatingScreen,
}: {
  membersData: MemberDataType[];
  plans: Plan[];
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  setShowActivatingScreen: (showActivatingScreen: boolean) => void;
}) {
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { isFeatureEnabled } = useFeatureFlag();

  const enableAsyncActivation = isFeatureEnabled(
    MIGRATION_ASSISTANT_ASYNC_ACTIVATION,
  );

  const { mutate } = useActivateMembers(enableAsyncActivation);

  const [selectedForActivationUsers, setSelectedForActivationUsers] = useState<
    number[]
  >([]);

  const [stepHistory, setStepHistory] = useState<StepType["id"][]>([
    StepIdType.WHO_TO_ACTIVATE,
  ]);
  const [currentStepId, setCurrentStepId] = useState<StepType["id"]>(
    StepIdType.WHO_TO_ACTIVATE,
  );
  const currentStepIndex = activationSteps().findIndex(
    (step) => step.id === currentStepId,
  );
  const currentStep = activationSteps()[currentStepIndex];

  const [activateSelection, setActivateSelection] = useState<
    StepType["id"] | undefined
  >(undefined);
  const [paymentSelection, setPaymentSelection] = useState<
    PaymentSelectionType | undefined
  >(undefined);

  const [selectedCollectPaymentUsers, setSelectedCollectPaymentUsers] =
    useState<number[]>([]);

  const [error, setError] = useState<string | null>(null);

  const selectedForActivationHasSubAccounts = selectedForActivationUsers.some(
    (userId) => membersData.find((member) => member.id === userId)?.parentEmail,
  );

  const activatingMembersData = useMemo(
    () =>
      membersData.filter((member) =>
        selectedForActivationUsers.some((userId) => userId === member.id),
      ),
    [membersData, selectedForActivationUsers],
  );

  const collectingPaymentPrimaryMembersData = useMemo(
    () =>
      membersData.filter((member) => {
        const isCollectingPayment = selectedCollectPaymentUsers.some(
          (userId) => userId === member.id,
        );
        const isPrimary = !member.parentEmail;
        return isCollectingPayment && isPrimary;
      }),
    [membersData, selectedCollectPaymentUsers],
  );

  const handleNext = (selection: StepType["id"]) => {
    const nextStepId = activationSteps().find(
      (step) => step.id === selection,
    ).id;
    setCurrentStepId(nextStepId);
    setStepHistory((prev) => [...prev, nextStepId]);
  };

  const handleBack = () => {
    if (stepHistory.length > 1) {
      const newHistory = stepHistory.slice(0, -1);
      setStepHistory(newHistory);
      setCurrentStepId(newHistory[newHistory.length - 1]);
      setError(null);
    }
  };

  const handleModalClose = () => {
    setSelectedForActivationUsers([]);
    setSelectedCollectPaymentUsers([]);
    setStepHistory([StepIdType.WHO_TO_ACTIVATE]);
    setCurrentStepId(StepIdType.WHO_TO_ACTIVATE);
    setActivateSelection(undefined);
    setPaymentSelection(undefined);
    setIsModalOpen(false);
    setError(null);
  };

  const handleActivate = async (
    activatingUserIds: number[],
    collectPaymentUserIds: number[],
  ) => {
    if (activatingUserIds.length === 0) {
      toast.error(
        "Please select at least one member to activate",
        defaultToastOptions,
      );
      return;
    }
    handleModalClose();
    setShowActivatingScreen(true);

    trackEvent("Migration Assistant Activate Members", {
      async_activation: enableAsyncActivation,
      activate_count: activatingUserIds.length,
      collect_payment_count: collectPaymentUserIds.length,
    });

    const data: { id: number; collectPayment: boolean }[] = Array.from(
      new Set([...activatingUserIds, ...collectPaymentUserIds]),
    ).map((id) => ({
      id,
      collectPayment: collectingPaymentPrimaryMembersData.some(
        (member) => member.id === id,
      ),
    }));

    mutate(
      {
        data,
        notifyUser: true,
      },
      {
        onSuccess: () => {
          if (!enableAsyncActivation) {
            toast.success("Members activated", defaultToastOptions);
            queryClient.invalidateQueries({
              queryKey: ["fetchMigratingMembers"],
            });
            setShowActivatingScreen(false);
          }
        },
        onError: (error) => {
          trackEvent("Migration Assistant Error", {
            async_activation: enableAsyncActivation,
            error: "ma_api_activate_members_error",
            api_error: JSON.stringify(error),
          });
          toast.error(
            "There was an error activating members",
            defaultToastOptions,
          );
          setShowActivatingScreen(false);
        },
      },
    );
  };

  const handleSelectActivationStep = () => {
    if (!activateSelection) {
      setError("Selection is required");
      return;
    }
    setSelectedForActivationUsers(
      activateSelection === StepIdType.SELECT_MEMBERS_TABLE
        ? []
        : membersData.map((member) => member.id),
    );
    setPaymentSelection(undefined);
    handleNext(activateSelection);
    setError(null);
  };

  const handleMembersTableStep = () => {
    if (!selectedForActivationUsers.length) {
      setError("Selection is required");
      return;
    }
    setPaymentSelection(undefined);
    handleNext(StepIdType.WHO_TO_PROMPT);
    setError(null);
  };

  const handlePaymentSelectionStep = () => {
    if (!paymentSelection) {
      setError("Selection is required");
      return;
    }
    const paymentStepActions: Record<PaymentSelectionType, () => void> = {
      [PaymentSelection.SELECT_PAYMENT_COLLECTION_TABLE]: () => {
        setSelectedCollectPaymentUsers([]);
        handleNext(StepIdType.SELECT_PAYMENT_COLLECTION_TABLE);
      },
      [PaymentSelection.REVIEW_AND_ACTIVATE_ALL]: () => {
        setSelectedCollectPaymentUsers(
          activatingMembersData.map((member) => member.id),
        );
        handleNext(StepIdType.REVIEW_AND_ACTIVATE);
      },
      [PaymentSelection.REVIEW_AND_ACTIVATE_NONE]: () => {
        setSelectedCollectPaymentUsers([]);
        handleNext(StepIdType.REVIEW_AND_ACTIVATE);
      },
    };
    const action =
      paymentStepActions[paymentSelection] ||
      (() => handleNext(StepIdType.REVIEW_AND_ACTIVATE));

    action();
    setError(null);
  };

  const handleMemberPaymentCollectionStep = () => {
    if (!selectedCollectPaymentUsers.length) {
      setError("Selection is required");
      return;
    }
    handleNext(StepIdType.REVIEW_AND_ACTIVATE);
    setError(null);
  };

  const stepHandlers = (): Record<number, () => void> => ({
    0: handleSelectActivationStep,
    1: handleMembersTableStep,
    2: handlePaymentSelectionStep,
    3: handleMemberPaymentCollectionStep,
    4: () =>
      handleActivate(selectedForActivationUsers, selectedCollectPaymentUsers),
  });

  return (
    <Dialog open={isModalOpen} onOpenChange={handleModalClose} modal>
      <DialogContent
        className="sm:tw-max-w-[680px] tw-w-full tw-rounded-3xl tw-shadow-lg"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <div className="tw-bg-white tw-rounded-3xl">
          <DialogHeader className="tw-pb-4 tw-border-b">
            <DialogTitle className="tw-text-lg tw-text-gray-900 tw-text-center tw-pt-0">
              {currentStep.title}
            </DialogTitle>
          </DialogHeader>
          <div className="tw-px-6 tw-py-6">
            <Stepper currentStep={currentStep} />
          </div>
          <div className="tw-pb-6">
            <ScrollArea className="tw-max-h-80 tw-w-auto tw-overflow-y-auto">
              {error && (
                <div className="sm:tw-mb-4 tw-mb-2 tw-p-2 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-text-red-600 tw-text-sm">
                  {error}
                </div>
              )}
              {currentStepIndex === 0 && (
                <SelectActivatingUsers
                  activateSelection={activateSelection}
                  setActivateSelection={setActivateSelection}
                  membersData={membersData}
                  error={error}
                />
              )}
              {currentStepIndex === 1 && (
                <MembersTable
                  membersData={membersData}
                  showSubAccount
                  selectedUsers={selectedForActivationUsers}
                  setSelectedUsers={setSelectedForActivationUsers}
                />
              )}
              {currentStepIndex === 2 && (
                <SelectPaymentCollectionUsers
                  paymentSelection={paymentSelection}
                  setPaymentSelection={setPaymentSelection}
                  selectedForActivationUsersCount={
                    selectedForActivationUsers.length
                  }
                  error={error}
                />
              )}
              {currentStepIndex === 3 && (
                <MembersTable
                  membersData={activatingMembersData}
                  plans={plans}
                  showPlan
                  selectedUsers={selectedCollectPaymentUsers}
                  setSelectedUsers={setSelectedCollectPaymentUsers}
                />
              )}
              {currentStepIndex === 4 && (
                <ReviewAndActivate
                  activatingUsersCount={selectedForActivationUsers.length}
                  collectingPaymentUsersCount={
                    selectedCollectPaymentUsers.length
                  }
                  hasSubAccounts={selectedForActivationHasSubAccounts}
                />
              )}
            </ScrollArea>
          </div>
          <DialogFooter className="tw-flex tw-flex-row sm:tw-justify-between tw-border-t tw-pt-6">
            <div>
              {currentStepIndex > 0 ? (
                <Button
                  variant="secondary"
                  className="tw-w-full sm:tw-w-auto"
                  size="lg"
                  onClick={() => handleBack()}
                >
                  Back
                </Button>
              ) : (
                <div />
              )}
            </div>
            <div>
              <Button
                size="lg"
                className="tw-w-full sm:tw-w-auto"
                onClick={() => {
                  const handler = stepHandlers()[currentStepIndex];
                  if (handler) {
                    handler();
                  }
                }}
              >
                {currentStepIndex === 4 ? "Activate" : "Next"}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
